import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link, Navigate, NavLink, Outlet, useParams } from "react-router";
import { formatRelative, formatVnd } from "../../../shared/i18n/index.ts";
import { ApiError, api } from "../../lib/api.ts";
import { serverNow } from "../../lib/clock.ts";
import { useI18n } from "../../lib/i18n.tsx";
import { useMe } from "../../lib/me.tsx";

/** /admin — plain tables, no design investment beyond legibility (08-design.md §Admin). */
export function AdminShell() {
  const { me, loading } = useMe();
  if (loading) return <div className="page muted">…</div>;
  if (!me || me.role !== "admin") return <Navigate to="/" replace />;
  const tabs = [
    ["users", "Users"],
    ["listings", "Listings"],
    ["purchases", "Purchases"],
    ["queues", "Queues"],
    ["invites", "Invites"],
    ["taxonomy", "Categories & Cities"],
    ["metrics", "Metrics"],
  ] as const;
  return (
    <div className="page admin">
      <nav className="chips" aria-label="admin">
        {tabs.map(([k, label]) => (
          <NavLink
            key={k}
            to={`/admin/${k}`}
            className={({ isActive }) => `chip${isActive ? " is-on" : ""}`}
          >
            {label}
          </NavLink>
        ))}
      </nav>
      <Outlet />
    </div>
  );
}

type Row = Record<string, unknown>;
const cell = (v: unknown): ReactNode =>
  v === null || v === undefined ? "—" : typeof v === "object" ? JSON.stringify(v) : String(v);

function Table({ rows, cols, link }: { rows: Row[]; cols: string[]; link?: (r: Row) => string }) {
  if (rows.length === 0) return <p className="muted small">0</p>;
  return (
    <div className="tablewrap">
      <table className="table">
        <thead>
          <tr>
            {cols.map((c) => (
              <th key={c}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={String(r.id ?? i)}>
              {cols.map((c) => (
                <td key={c}>
                  {link && c === cols[0] ? <Link to={link(r)}>{cell(r[c])}</Link> : cell(r[c])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Search({
  path,
  cols,
  link,
  placeholder,
}: {
  path: string;
  cols: string[];
  link: (r: Row) => string;
  placeholder: string;
}) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState<Row[]>([]);
  async function go(e: FormEvent) {
    e.preventDefault();
    const r = await api<{ items: Row[] }>(`${path}?q=${encodeURIComponent(q)}`).catch(() => ({
      items: [],
    }));
    setRows(r.items);
  }
  return (
    <>
      <form className="row" onSubmit={go}>
        <input
          className="input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={placeholder}
          data-testid="admin-search"
        />
        <button className="btn" type="submit">
          Search
        </button>
      </form>
      <Table rows={rows} cols={cols} link={link} />
    </>
  );
}

export const AdminUsers = () => (
  <Search
    path="/api/admin/users"
    cols={["handle", "display_name", "phone", "status", "can_sell", "role"]}
    link={(r) => `/admin/users/${r.id}`}
    placeholder="handle or phone"
  />
);
export const AdminListings = () => (
  <Search
    path="/api/admin/listings"
    cols={["title", "seller_handle", "status", "hidden", "open_reports", "quantity_remaining"]}
    link={(r) => `/admin/listings/${r.id}`}
    placeholder="title, handle or id"
  />
);
export const AdminPurchases = () => (
  <Search
    path="/api/admin/purchases"
    cols={["id", "title", "buyer_handle", "seller_handle", "status", "total", "problem_open"]}
    link={(r) => `/admin/purchases/${r.id}`}
    placeholder="id, handle or title"
  />
);

function ActionBar({
  actions,
  onAct,
}: {
  actions: string[];
  onAct: (action: string, note: string) => Promise<void>;
}) {
  const [note, setNote] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  return (
    <div className="stack">
      <input
        className="input"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="note (audited)"
        data-testid="admin-note"
      />
      <div className="row">
        {actions.map((a) => (
          <button
            key={a}
            className="btn"
            onClick={() =>
              void onAct(a, note)
                .then(() => setMsg(`${a} ✓`))
                .catch((e: unknown) => setMsg(e instanceof ApiError ? e.body.error : "error"))
            }
            data-testid={`act-${a}`}
          >
            {a}
          </button>
        ))}
      </div>
      {msg ? <p className="muted small">{msg}</p> : null}
    </div>
  );
}

export function AdminUserDetail() {
  const { id = "" } = useParams();
  const [d, setD] = useState<Row | null>(null);
  const [cap, setCap] = useState("");
  const load = () =>
    api<Row>(`/api/admin/users/${id}`)
      .then(setD)
      .catch(() => setD(null));
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  if (!d) return <p className="muted">…</p>;
  const user = d.user as Row;
  return (
    <div className="stack">
      <h2 className="h2">
        @{String(user.handle)} · {String(user.display_name)} · {String(user.status)}
        {user.suspended_reason ? ` (${String(user.suspended_reason)})` : ""}
      </h2>
      <p className="small">
        phone {String(user.phone)} · can_sell {String(user.can_sell)} · cap{" "}
        {String(user.listing_cap)} · invited by {String(user.invited_by_handle ?? "—")}
      </p>
      <ActionBar
        actions={["suspend", "lift", "ban", "unban", "grant_sell", "revoke_sell"]}
        onAct={async (action, note) => {
          await api(`/api/admin/users/${id}/action`, { body: { action, note } });
          await load();
        }}
      />
      <form
        className="row"
        onSubmit={(e) => {
          e.preventDefault();
          void api(`/api/admin/users/${id}/listing-cap`, {
            method: "PUT",
            body: { listing_cap: Number(cap), note: "cap" },
          }).then(load);
        }}
      >
        <input
          className="input"
          type="number"
          value={cap}
          onChange={(e) => setCap(e.target.value)}
          placeholder="listing cap"
        />
        <button className="btn" type="submit">
          Set cap
        </button>
      </form>
      <h3>Strikes</h3>
      <Table
        rows={d.strikes as Row[]}
        cols={["reason", "purchase_id", "issued_by", "expires_at", "revoked_at"]}
      />
      <h3>Invites</h3>
      <Table rows={d.invites as Row[]} cols={["code", "label", "used_by_handle", "used_at"]} />
      <h3>Sessions</h3>
      <Table rows={d.sessions as Row[]} cols={["id", "ua", "last_seen_at"]} />
      <h3>Listings</h3>
      <Table
        rows={d.listings as Row[]}
        cols={["title", "status", "start_price", "quantity_remaining"]}
        link={(r) => `/admin/listings/${r.id}`}
      />
      <h3>Purchases</h3>
      <Table
        rows={d.purchases as Row[]}
        cols={["id", "status", "total"]}
        link={(r) => `/admin/purchases/${r.id}`}
      />
      <h3>Sales</h3>
      <Table
        rows={d.sales as Row[]}
        cols={["id", "status", "total"]}
        link={(r) => `/admin/purchases/${r.id}`}
      />
      <h3>History</h3>
      <Table rows={d.history as Row[]} cols={["action", "admin_handle", "note", "created_at"]} />
    </div>
  );
}

export function AdminListingDetail() {
  const { id = "" } = useParams();
  const [d, setD] = useState<Row | null>(null);
  const load = () =>
    api<Row>(`/api/admin/listings/${id}`)
      .then(setD)
      .catch(() => setD(null));
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  if (!d) return <p className="muted">…</p>;
  const l = d.listing as Row;
  return (
    <div className="stack">
      <h2 className="h2">
        {String(l.title)} · {String(l.status)} · hidden {String(l.hidden)} ·{" "}
        <Link to={`/l/${id}`}>view</Link>
      </h2>
      <p className="small">
        seller @{String(l.seller_handle)} · start {formatVnd(Number(l.start_price ?? 0))} · floor{" "}
        {formatVnd(Number(l.floor_price ?? 0))} · stock {String(l.quantity_remaining)}/
        {String(l.quantity)}
      </p>
      <ActionBar
        actions={["hide", "unhide", "cancel"]}
        onAct={async (action, note) => {
          await api(`/api/admin/listings/${id}/action`, { body: { action, note } });
          await load();
        }}
      />
      <h3>Reports</h3>
      <Table rows={d.reports as Row[]} cols={["reporter_handle", "reason", "note", "status"]} />
      <h3>Purchases</h3>
      <Table
        rows={d.purchases as Row[]}
        cols={["id", "buyer_handle", "status", "total"]}
        link={(r) => `/admin/purchases/${r.id}`}
      />
      <h3>History</h3>
      <Table rows={d.history as Row[]} cols={["action", "admin_handle", "note", "created_at"]} />
    </div>
  );
}

export function AdminPurchaseDetail() {
  const { id = "" } = useParams();
  const [d, setD] = useState<Row | null>(null);
  const load = () =>
    api<Row>(`/api/admin/purchases/${id}`)
      .then(setD)
      .catch(() => setD(null));
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  if (!d) return <p className="muted">…</p>;
  const p = d.purchase as Row;
  const problem = d.problem as Row | null;
  return (
    <div className="stack">
      <h2 className="h2">
        {String(p.title)} · {String(p.status)}
      </h2>
      <p className="small">
        buyer @{String(p.buyer_handle)} · seller @{String(p.seller_handle)} · {String(p.quantity)} ×{" "}
        {formatVnd(Number(p.unit_price))} · deal code {String(p.deal_code)} · problem_open{" "}
        {String(p.problem_open)}
      </p>
      <p className="small">
        created {String(p.created_at)} · shared {String(p.contact_shared_at)} · done marked{" "}
        {String(p.done_marked_at)} · completed {String(p.completed_at)} via{" "}
        {String(p.completed_via)} · cancelled {String(p.cancelled_at)} by {String(p.cancelled_by)}
      </p>
      <ActionBar
        actions={["force_cancel", "force_complete"]}
        onAct={async (action, note) => {
          await api(`/api/admin/purchases/${id}/action`, {
            body: { action, note: note || action },
          });
          await load();
        }}
      />
      {problem ? (
        <>
          <h3>Problem</h3>
          <Table
            rows={[problem]}
            cols={["reason", "note", "reply_note", "status", "resolution", "admin_note"]}
          />
        </>
      ) : null}
      <h3>History</h3>
      <Table rows={d.history as Row[]} cols={["action", "admin_handle", "note", "created_at"]} />
    </div>
  );
}

export function AdminQueues() {
  const { t } = useI18n();
  const [reports, setReports] = useState<Row[]>([]);
  const [problems, setProblems] = useState<Row[]>([]);
  const [suspended, setSuspended] = useState<Row[]>([]);
  const [note, setNote] = useState("");
  const load = async () => {
    const [r, p, s] = await Promise.all([
      api<{ items: Row[] }>("/api/admin/queues/reports"),
      api<{ items: Row[] }>("/api/admin/queues/problems"),
      api<{ items: Row[] }>("/api/admin/queues/suspended"),
    ]);
    setReports(r.items);
    setProblems(p.items);
    setSuspended(s.items);
  };
  useEffect(() => {
    // deferred a tick so the effect itself never sets state synchronously
    void Promise.resolve().then(load);
  }, []);
  const now = serverNow();
  return (
    <div className="stack">
      <input
        className="input"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="resolution note (shown to both parties for problems)"
        data-testid="queue-note"
      />
      <h3>Reports ({reports.length})</h3>
      {reports.map((r) => (
        <div key={String(r.listing_id)} className="sheet" data-testid="queue-report">
          <div>
            <Link to={`/admin/listings/${String(r.listing_id)}`}>{String(r.title)}</Link> · @
            {String(r.seller_handle)} · {String(r.open)} open · {(r.reasons as string[]).join(", ")}{" "}
            · hidden {String(r.hidden)}
          </div>
          <div className="row">
            {["dismiss", "hide_listing", "cancel_listing"].map((a) => (
              <button
                key={a}
                className="btn"
                onClick={() =>
                  void api(`/api/admin/reports/${String(r.listing_id)}/resolve`, {
                    body: { action: a, note },
                  }).then(load)
                }
                data-testid={`report-${a}`}
              >
                {a}
              </button>
            ))}
          </div>
        </div>
      ))}
      <h3>Problems ({problems.length})</h3>
      {problems.map((p) => (
        <div key={String(p.id)} className="sheet" data-testid="queue-problem">
          <div>
            <Link to={`/admin/purchases/${String(p.purchase_id)}`}>{String(p.title)}</Link> ·{" "}
            {String(p.purchase_status)} · {formatVnd(Number(p.total))} ·{" "}
            {formatRelative(Number(p.created_at), now, "en")}
          </div>
          <div className="small">
            @{String(p.reporter_handle)}: <strong>{String(p.reason)}</strong> — {String(p.note)}
          </div>
          {p.reply_note ? <div className="small quote">{String(p.reply_note)}</div> : null}
          <div className="row">
            {[
              "no_action",
              "strike_buyer",
              "strike_seller",
              "cancel_purchase",
              "complete_purchase",
            ].map((a) => (
              <button
                key={a}
                className="btn"
                disabled={!note.trim()}
                onClick={() =>
                  void api(`/api/admin/problems/${String(p.id)}/resolve`, {
                    body: { resolution: a, note },
                  }).then(load)
                }
                data-testid={`problem-${a}`}
              >
                {a}
              </button>
            ))}
          </div>
        </div>
      ))}
      <h3>
        {t("profile.strikes")} → suspended ({suspended.length})
      </h3>
      {suspended.map((u) => (
        <div key={String(u.id)} className="sheet" data-testid="queue-suspended">
          <div>
            <Link to={`/admin/users/${String(u.id)}`}>@{String(u.handle)}</Link> ·{" "}
            {String(u.active_strikes)} active strikes · {String(u.suspended_reason)}
          </div>
          <div className="row">
            {["lift", "ban"].map((a) => (
              <button
                key={a}
                className="btn"
                onClick={() =>
                  void api(`/api/admin/users/${String(u.id)}/action`, {
                    body: { action: a, note },
                  }).then(load)
                }
                data-testid={`suspended-${a}`}
              >
                {a}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function AdminInvites() {
  const [label, setLabel] = useState("drop-1-seed");
  const [count, setCount] = useState("20");
  const [rows, setRows] = useState<Row[]>([]);
  const load = (l: string) =>
    api<{ items: Row[] }>(`/api/admin/invites?label=${encodeURIComponent(l)}`)
      .then((r) => setRows(r.items))
      .catch(() => setRows([]));
  return (
    <div className="stack">
      <form
        className="row"
        onSubmit={(e) => {
          e.preventDefault();
          void api(`/api/admin/invites`, { body: { count: Number(count), label } }).then(() =>
            load(label),
          );
        }}
      >
        <input
          className="input"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="label"
          data-testid="invite-label"
        />
        <input
          className="input"
          type="number"
          value={count}
          onChange={(e) => setCount(e.target.value)}
          min={1}
          max={500}
        />
        <button className="btn btn--accent" type="submit" data-testid="invite-generate">
          Generate
        </button>
        <button className="btn" type="button" onClick={() => void load(label)}>
          Load
        </button>
      </form>
      <Table rows={rows} cols={["code", "label", "owner_handle", "used_by_handle", "used_at"]} />
    </div>
  );
}

export function AdminTaxonomy() {
  const [cats, setCats] = useState<Row[]>([]);
  const [cities, setCities] = useState<Row[]>([]);
  const load = async () => {
    setCats((await api<{ items: Row[] }>("/api/admin/categories")).items);
    setCities((await api<{ items: Row[] }>("/api/admin/cities")).items);
  };
  useEffect(() => {
    // deferred a tick so the effect itself never sets state synchronously
    void Promise.resolve().then(load);
  }, []);
  const toggle = (kind: "categories" | "cities", r: Row) =>
    api(`/api/admin/${kind}/${String(r.id)}`, {
      method: "PUT",
      body: {
        ...(kind === "categories" ? { parent_id: r.parent_id ?? null } : {}),
        slug: r.slug,
        name_vi: r.name_vi,
        name_en: r.name_en,
        enabled: !(Number(r.enabled) === 1),
        sort_order: Number(r.sort_order),
      },
    }).then(load);
  const list = (kind: "categories" | "cities", rows: Row[]) => (
    <ul className="list">
      {rows.map((r) => (
        <li key={String(r.id)} className="list__row">
          <span>
            {String(r.name_vi)} / {String(r.name_en)}{" "}
            <span className="hint">
              {String(r.slug)} · #{String(r.sort_order)}
              {kind === "categories" && r.parent_id ? ` · parent ${String(r.parent_id)}` : ""}
            </span>
          </span>
          <button className="btn btn--ghost" onClick={() => void toggle(kind, r)}>
            {Number(r.enabled) === 1 ? "disable" : "enable"}
          </button>
        </li>
      ))}
    </ul>
  );
  return (
    <div className="stack">
      <h3>Categories</h3>
      {list("categories", cats)}
      <h3>Cities</h3>
      {list("cities", cities)}
    </div>
  );
}
